'use client';

import React, { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes } from 'react';
import { MoreHorizontalIcon } from 'lucide-react';
import { useSession } from 'next-auth/react';

import { showInfoToast, showSuccessToast, showWarningToast } from '@/components/ui/sonner';
import { getChaptersContentByWorkIdReq } from '@/features/fanfic-page/requests/chapters.requests';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface FanficDropdownProps {
  workId: string;
  fanficTitle: string;
  chaptersCount: number;
  className?: string;
  workPathPrefix?: '/fanfics' | '/originals';
}

const FanficDropdownTrigger = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { extraClassName?: string }
>(function FanficDropdownTrigger({ extraClassName = '', className, ...props }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      {...props}
      className={`w-[40px] h-[40px] max-[999px]:w-[48px] max-[999px]:h-[48px] bg-white/10 text-white hover:bg-white/20 p-3 rounded-xl border border-white/50 flex items-center justify-center transition-colors ${extraClassName} ${className ?? ''}`}
    >
      <MoreHorizontalIcon className="w-5 h-5" />
    </button>
  );
});

export function FanficDropdown({
  workId,
  fanficTitle,
  chaptersCount,
  className = '',
  workPathPrefix = '/fanfics',
}: FanficDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverReady, setPopoverReady] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const { data: session } = useSession();
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);

  useEffect(() => {
    setPopoverReady(true);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      touchStartRef.current = null;
      return;
    }

    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const handleTouchStart = (e: TouchEvent) => {
      if (window.innerWidth >= 768) return;

      const touch = e.touches[0];
      if (touch) {
        touchStartRef.current = {
          x: touch.clientX,
          y: touch.clientY,
          time: Date.now(),
        };
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (window.innerWidth >= 768) return;

      if (!touchStartRef.current) return;

      const touch = e.touches[0];
      if (!touch) return;

      const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
      const timeDelta = Date.now() - touchStartRef.current.time;

      const threshold = isIOS ? 8 : 10;

      if ((deltaX > threshold || deltaY > threshold) && timeDelta < 300) {
        setIsOpen(false);
        touchStartRef.current = null;
      }
    };

    const handleTouchEnd = () => {
      touchStartRef.current = null;
    };

    const handleTouchCancel = () => {
      if (isIOS && touchStartRef.current) {
        setIsOpen(false);
      }
      touchStartRef.current = null;
    };

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchCancel, {
      passive: true,
    });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [isOpen]);

  const handleShare = async () => {
    const url = `${window.location.origin}${workPathPrefix}/${workId}`;
    try {
      await navigator.clipboard.writeText(url);
      showSuccessToast('Ссылка скопирована в буфер обмена');
    } catch (err) {
      console.error('Ошибка при копировании:', err);
      showWarningToast('Не удалось скопировать ссылку');
    }
    setIsOpen(false);
  };

  const handleDownload = async () => {
    if (isDownloading) return;

    setIsDownloading(true);
    setIsOpen(false);
    showInfoToast('Загрузка глав для скачивания...');

    try {
      // Load all chapters with content
      const response = await getChaptersContentByWorkIdReq({
        workId,
        page: 1,
        limit: chaptersCount,
      });

      console.log('response:', response);

      const chapters = response.chapters;

      if (!chapters || chapters.length === 0) {
        showWarningToast('Нет глав для скачивания');
        setIsDownloading(false);
        return;
      }

      let fullText = `${fanficTitle}\n\n`;

      for (let i = 0; i < chapters.length; i++) {
        const chapter = chapters[i];
        fullText += `Глава ${i + 1}: ${chapter.title || `Глава ${i + 1}`}\n\n`;

        if (chapter.content) {
          const textContent = extractTextFromLexical(chapter.content);
          fullText += textContent + '\n\n';
        }
      }

      const blob = new Blob([fullText], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${fanficTitle.replace(/[^a-zA-Zа-яА-Я0-9\s]/g, '_')}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      showSuccessToast('Фанфик успешно скачан');
    } catch (err) {
      console.error('Ошибка при скачивании:', err);
      showWarningToast('Не удалось скачать фанфик');
    } finally {
      setIsDownloading(false);
    }
  };

  const extractTextFromLexical = (content: any): string => {
    try {
      if (!content) return '';

      if (typeof content === 'string') {
        return content.replace(/<[^>]*>/g, '');
      }

      const root = content.root ?? content;
      const result: string[] = [];

      const walk = (node: any) => {
        if (!node || typeof node !== 'object') return;

        if (typeof node.text === 'string' && node.text.trim().length > 0) {
          result.push(node.text);
        }

        if (Array.isArray(node.children)) {
          for (const child of node.children) {
            walk(child);
          }
        }
      };

      walk(root);
      return result.join(' ');
    } catch (error) {
      console.error('Ошибка при извлечении текста из Lexical:', error);
      return '';
    }
  };

  const handleReport = () => {
    const subject = encodeURIComponent(`Жалоба на фанфик: ${fanficTitle}`);
    const body = encodeURIComponent(
      `Ссылка на фанфик: ${window.location.href}\n\nОписание проблемы:`,
    );
    window.open(`mailto:info@molwe.ru?subject=${subject}&body=${body}`);
    setIsOpen(false);
  };

  const trigger = (
    <FanficDropdownTrigger extraClassName={className} onClick={() => setIsOpen(!isOpen)} />
  );

  if (!popoverReady) {
    return trigger;
  }

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent className="max-w-[210px] p-2 rounded-[12px] bg-[#F2F3F7]" align="end">
          <div className="grid gap-1">
            <button
              onClick={() => {
                handleShare();
              }}
              className="w-full max-h-[34px]  px-2 py-[7px] border rounded-lg  border-transparent text-left hover:bg-white dark:hover:bg-gray-700 flex items-center justify-between transition-colors"
            >
              Поделиться
            </button>
            {!!session && (
              <button
                onClick={handleDownload}
                disabled={isDownloading}
                className="w-full max-h-[34px]  px-2 py-[7px] border rounded-lg  border-transparent text-left hover:bg-white dark:hover:bg-gray-700 flex items-center justify-between transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloading ? 'Загрузка...' : 'Скачать'}
              </button>
            )}
            <button
              onClick={handleReport}
              className="w-full max-h-[34px]  px-2 py-[7px] border rounded-lg  border-transparent text-left hover:bg-white dark:hover:bg-gray-700 flex items-center justify-between transition-colors"
            >
              Пожаловаться
            </button>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
